import assign from 'lodash/assign';
import get from 'lodash/get';
import omit from 'lodash/omit';
import moment from 'moment';
import startsWith from 'lodash/startsWith';
import uuid from 'uuid-v4';

import { AppSettingsModule } from 'services/settings.provider';
import { DataServiceModule } from 'services/data/data.service';
import { ExpireCacheServiceModule } from 'services/utilities/expireCache.service';
import { ModelServiceModule } from 'services/data/model/model.service';
import { StoreInformationServiceModule } from 'services/data/storeInformation.service';

export const VolusionPaymentsServiceModule = angular
    .module('app.services.data.volusionPayments', [
        DataServiceModule,
        ExpireCacheServiceModule,
        ModelServiceModule,
        AppSettingsModule,
        StoreInformationServiceModule
    ]).service('volusionPaymentsService', (
        dataService,
        expireCache,
        modelService,
        storeInformationService,
        settings,
        $window,
        $q,
        $timeout
    ) => {
        return {
            ADDRESS_TYPE: {
                RENT: 'rent',
                OWN: 'own'
            },
            BUSINESS_DESCRIPTION_MAX_LEN: 255,
            BUSINESS_TYPE: {
                INDIVIDUAL: 'Individual/Sole Proprietor',
                PARTNERSHIP: 'Partnership',
                CORPORATION: 'Corporation',
                ORGANIZATION: 'Tax Exempt Organization',
                LLC: 'Limited Liability Company (LLC)',
                UNKNOWN: 'Unknown'
            },
            DELIVERY_TIMES: [
                'Instantly over the internet',
                '0-2 Days',
                '3 to 5 Days',
                '6 to 10 Days',
                'Greater than 10'
            ],
            DESCRIPTOR_MAX_LEN: 22,
            SALES_RANGES: [
                'Under $50K',
                '$50K - $100K',
                '$100K - $150K',
                '$150K - $250K',
                '$250K - $500K',
                'Over $500K'
            ],
            TYPES_OF_GOODS: [
                'Art',
                'Automotive',
                'Entertainment',
                'Clothing & Accessories',
                'Food & Drink',
                'Craft & Hobby',
                'Electronics',
                'Games',
                'Health & Beauty',
                'Home Decor',
                'Jewelry',
                'Kids & Baby',
                'Kitchen',
                'Lifestyle',
                'Pet Supplies',
                'Services',
                'Sports & Outdoors',
                'Toys & Collectibles',
                'Weddings',
                'Other'
            ],
            formFields: {
                BUSINESS_IDENTITY: [
                    'legalName', 'businessType', 'ein', 'website', 'phoneNumber1',
                    'address11', 'city1', 'zip1', 'state1'
                ],
                ABOUT_BUSINESS: [
                    'description', 'yearsInBusiness', 'averageTime', 'averageOrder', 'annualSales'
                ],
                BUSINESS_OWNER: [
                    'ownerTitle1', 'ownerFirstName1', 'ownerLastName1',
                    'address12', 'city2', 'zip2', 'state2',
                    'addressType', 'yearsAtAddress',
                    'email', 'phoneNumber2',
                    'year1', 'month1', 'day1',
                    'socialSecurityNumber1'
                ],
                CARD_STATEMENT: [
                    'statementDescriptor', 'phoneNumber'
                ],
                BANKING: [
                    'routingNumber', 'bankAccount', 'termsAccepted'
                ]
            },
            steps: {
                BUSINESS_IDENTITY: 1,
                ABOUT_BUSINESS: 2,
                BUSINESS_OWNER: 3,
                CARD_STATEMENT: 4,
                BANKING: 5
            },
            userStates: {
                SIGN_UP: 0,
                READY_FOR_OAUTH: 1,
                COMPLETE: 2
            },
            errors: {
                ADDITIONAL_OWNERS: 'Invalid parameter business.us_entity_compliance.additional_beneficial_owners',
                BUSINESS: 'Invalid parameter business.account_owner',
                INDIVIDUAL: 'Invalid parameter individual.account_owner',
                SSN_LAST4: 'social_security_number_last_4',
                PHONE: '.phone',
                FULL_SSN: 'A 9-digit social security number is required for this account.',
                ZIP_CODE: 'Malformed address (ZIP Code',
                EIN: 'Invalid parameter business.us_entity_compliance.ein',
                CONTACT_NUMBER: 'Invalid parameter support_contact_number',
                ROUTING_NUMBER: 'Invalid parameter us_institution.routing_number'
            },
            paymentProviders: {
                VOLUSION_PAYMENTS: 'Voluion Payments',
                AUTHORIZE_NET: 'Authorize.Net',
                STRIPE: 'Stripe'
            },
            usTerritories: ['AS', 'FM', 'GU', 'MH', 'MP', 'PR', 'PW', 'VI'],
            defaultDebounce: 750,
            badValueCheck,
            bankAccountRegex,
            bankRoutingRegex,
            basicDialogController,
            completeOauth,
            disconnectAuthorizeNet,
            formatKycData,
            getActiveProvider,
            getBadgeText,
            getLoginUrl,
            getOauthError,
            getOauthUrl,
            getPrePopulationData,
            getTestModeUrl,
            getUserState,
            goToAuthorizeNetOauth,
            hasAccount,
            hasOauthConnection,
            isInTestMode,
            isKYCComplete,
            isPaymentSignupReminderNeeded,
            shouldShowAuthorizeNet,
            isStepValid,
            isValidBankAccount,
            isValidBillingStatementDescriptor,
            isValidBusinessDescription,
            isValidDateOfBirth,
            isValidEIN,
            isValidEmail,
            isValidLegalEntityName,
            isValidName,
            isValidNumber,
            isValidPhone,
            isValidPostalCode,
            isValidRoutingNumber,
            isValidSocialSecurity,
            isValidStreetAddress,
            isValidUrl,
            isWhitelisted,
            makeKYCModel,
            molestField,
            molestFields,
            prePopulateAddress,
            prePopulateKYCModel,
            refreshStoreInfo,
            removeUnsupportedStates,
            saveAccountCreated,
            saveAccountDenied,
            savePaymentsSignupStarted,
            setActiveProvider,
            setIsEnabled,
            setShouldShowConnectedMessage,
            showFieldErrors,
            submitKyc,
            updateAuthorizeNetEnabled,
            updateSignupSubmitted,
            updateStripePreferred,
            validateAboutBusiness,
            validateBanking,
            validateBusinessIdentity,
            validateCardStatement,
            validateKYC,
            validateOwner
        };

        function submitKyc(kycData) {
            const formattedData = formatKycData(kycData);
            return dataService.create(settings.api.volusionPaymentsKYC, formattedData)
                .then(this.updateSignupSubmitted.bind(this));
        }

        function formatKycData(kycData) {
            const data = JSON.parse(JSON.stringify(kycData));
            return clean(data);
        }

        function clean(obj) {
            const propNames = Object.keys(obj);
            for (let i = 0; i < propNames.length; i++) {
                if (isBadObjValue(obj[propNames[i]]) || propNames[i] === '$$hashKey') {
                    delete obj[propNames[i]];
                } else if (typeof obj[propNames[i]] === 'object' && obj[propNames[i]] !== null) {
                    obj[propNames[i]] = clean(obj[propNames[i]]);
                }
            }
            return obj;
        }

        function isBadObjValue(val) {
            return val === undefined || val === '';
        }

        function isKYCComplete(storeInfo) {
            return get(storeInfo, 'authorizeNet.isPaymentsSignupSubmitted');
        }

        // KYC Validation (entire form)
        function validateKYC(inputModel) {
            const model = inputModel || {};
            const businessIdentityValidation = validateBusinessIdentity(model.business_identity);
            const aboutBusinessValidation = validateAboutBusiness(model.about_business);
            const ownerValidation = validateOwner(model.owner);
            const cardStatementValidation = this.validateCardStatement(model.card_statement);
            const bankingValidation = validateBanking(model.banking);
            const steps = {
                businessIdentityValidation,
                aboutBusinessValidation,
                ownerValidation,
                cardStatementValidation,
                bankingValidation
            };
            const isValid = businessIdentityValidation.isValid && aboutBusinessValidation.isValid &&
                ownerValidation.isValid && cardStatementValidation.isValid && bankingValidation.isValid;

            return {
                isValid,
                steps,
                props: {
                    ...businessIdentityValidation.props,
                    ...aboutBusinessValidation.props,
                    ...ownerValidation.props,
                    ...cardStatementValidation.props,
                    ...bankingValidation.props
                }
            };
        }

        // KYC Step 1: BUSINESS_IDENTITY
        function validateBusinessIdentity(inputIdentity) {
            const identity = inputIdentity || {};
            identity.address = identity.address || {};
            identity.business_phone = identity.business_phone || {};

            const isBusinessNameValid = isNonEmptyString(identity.business_name); // required
            const isEINValid = isValidEIN(identity.employer_id_number);
            const isWebsiteValid = isValidUrl(identity.business_website);
            const isPhoneValid = isValidPhone(identity.business_phone); // required
            const isPostalCodeValid = isValidPostalCode(identity.address.postal_code); // required
            const isCountryValid = isValidCountry(identity.address.country); // required

            const isValid = isBusinessNameValid && isEINValid && isWebsiteValid && isPhoneValid &&
                isPostalCodeValid && isCountryValid;
            return {
                isValid,
                props: {
                    business_name: isBusinessNameValid,
                    business_phone: isPhoneValid,
                    business_postal_code: isPostalCodeValid,
                    business_website: isWebsiteValid,
                    country: isCountryValid,
                    employer_id_number: isEINValid
                }
            };
        }

        // KYC Step 2: ABOUT_BUSINESS
        function validateAboutBusiness(inputAbout) {
            const about = inputAbout || {};

            const isValidYearsInBusiness = isValidNumber(about.years_in_business);
            const isValidAverageOrder = isValidNumber(about.average_order);

            const isValid = isValidYearsInBusiness && isValidAverageOrder;
            return {
                isValid,
                props: {
                    average_order: isValidAverageOrder,
                    years_in_business: isValidYearsInBusiness
                }
            };
        }

        // KYC Step 3: BUSINESS_OWNER
        function validateOwner(inputOwner) {
            const owner = inputOwner || {};
            owner.owner_address = owner.owner_address || {};
            owner.owner_phone = owner.owner_phone || {};

            const REQUIRED = true;
            const isNameValid = isValidName(owner.owner_name); // required
            const isPostalCodeValid = isValidPostalCode(owner.owner_address.postal_code); // required
            const isPhoneValid = isValidPhone(owner.owner_phone); // required
            const isOwnershipTypeValid = isValidOwnershipType(owner.ownership_type); // required
            const isSocialSecurityValid = isValidSocialSecurity(owner.social_security_number);
            const isEmailValid = isValidEmail(owner.email, REQUIRED); // required
            const isDateOfBirthValid = isValidDateOfBirth(owner.date_of_birth);
            const isYearsAtAddressValid = isValidNumber(owner.owner_years_at_address, REQUIRED); // required

            const isValid = isPostalCodeValid && isPhoneValid && isOwnershipTypeValid && isNameValid &&
                isSocialSecurityValid && isEmailValid && isDateOfBirthValid && isYearsAtAddressValid;
            return {
                isValid,
                props: {
                    date_of_birth: isDateOfBirthValid,
                    email: isEmailValid,
                    owner_name: isNameValid,
                    owner_phone: isPhoneValid,
                    owner_postal_code: isPostalCodeValid,
                    owner_years_at_address: isYearsAtAddressValid,
                    ownership_type: isOwnershipTypeValid,
                    social_security: isSocialSecurityValid
                }
            };
        }

        // KYC Step 4: CARD_STATEMENT
        function validateCardStatement(inputStatement) {
            const statement = inputStatement || {};

            const isBillingDescriptorValid =
                this.isValidBillingStatementDescriptor(statement.billing_statement_descriptor);
            const isCustomerServicePhoneValid = isValidPhone(statement.customer_service_phone); // required
            const isValid = isBillingDescriptorValid && isCustomerServicePhoneValid;
            return {
                isValid,
                props: {
                    billing_statement_descriptor: isBillingDescriptorValid,
                    customer_service_phone: isCustomerServicePhoneValid
                }
            };
        }

        // KYC Step 5: BANKING
        function validateBanking(inputBanking) {
            const banking = inputBanking || {};

            const isBankAccountValid = isValidBankAccount(banking.account_number); // required
            const isRoutingNumberValid = isValidRoutingNumber(banking.routing_number); // required
            const isTermsOfServiceValid = !!banking.terms_of_service; // required
            const isValid = isBankAccountValid && isRoutingNumberValid && isTermsOfServiceValid;
            return {
                isValid,
                props: {
                    account_number: isBankAccountValid,
                    routing_number: isRoutingNumberValid,
                    terms_of_service: isTermsOfServiceValid
                }
            };
        }

        function isNonEmptyString(string) {
            return !!string && (typeof string === 'string');
        }

        function isValidName(name) {
            return !!name && isNonEmptyString(name.first) && isNonEmptyString(name.last);
        }

        function isValidLegalEntityName(name) {
            return !!name && isNonEmptyString(name) && name.length < 256;
        }

        function isValidStreetAddress(address) {
            return !!address.address1 && isNonEmptyString(address.address1);
        }

        function isValidCountry(country) {
            return country === 'US';
        }

        function removeUnsupportedStates(usStates) {
            return omit(usStates, this.usTerritories);
        }

        function isValidPostalCode(code) {
            return isNonEmptyString(code) && /^\d{5}(?:[-\s]\d{4})?$/.test(code);
        }

        function isValidPhone(phone) {
            const defined = !!phone && isNonEmptyString(phone.country_code) && isNonEmptyString(phone.phone_number);
            if (!defined) return false;
            const re = /^\s*(?:\+?(\d{1,3}))?[-. (]*(\d{3})[-. )]*(\d{3})[-. ]*(\d{4})(?: *x(\d+))?\s*$/;
            return re.test(phone.phone_number);
        }

        function isValidBusinessDescription(description) {
            if (description) return description.length <= this.BUSINESS_DESCRIPTION_MAX_LEN;
            return true;
        }

        function isValidDateOfBirth(date, isRequired) {
            const isEmpty = date && !date.year && !date.month && !date.day;
            if (isEmpty && !isRequired) return true;
            if (!date || !date.year || !date.month || !date.day) return false;

            const newDateFromGivenDate = new Date(date.year, date.month - 1, date.day);
            const validDate = (
                newDateFromGivenDate.getFullYear() === date.year &&
                newDateFromGivenDate.getMonth() + 1 === date.month &&
                newDateFromGivenDate.getDate() === date.day
            );

            const momentFromGivenDate = moment({ year: date.year, month: date.month - 1, day: date.day });
            const oneHundredYearsAgo = moment().subtract(100, 'years');
            const eighteenYearsAgo = moment().subtract(18, 'years');

            const tooOld = momentFromGivenDate.isBefore(oneHundredYearsAgo);
            const tooYoung = momentFromGivenDate.isAfter(eighteenYearsAgo);

            return validDate && !tooOld && !tooYoung;
        }

        function isValidOwnershipType(type) {
            return !!type;
        }

        function isValidEmail(email, isRequired) {
            if (!email && !isRequired) return true;
            const re = /.+@.+\.[^\s]+/;
            return isNonEmptyString(email) && re.test(String(email).toLowerCase());
        }

        function isValidSocialSecurity(ssn, isRequired) {
            if (!ssn && !isRequired) return true;
            const testSsn = ssn || '';

            return /^[0-9]{3}-[0-9]{2}-[0-9]{4}$/.test(testSsn); // nnn-nn-nnnn
        }

        function isValidEIN(ein, isRequired) {
            if (!ein && !isRequired) return true;
            const testEin = ein || '';

            // nnnnnnnnn, n-nnnnnnnn, or nn-nnnnnnn
            const isEinValid = /^([0-9]{1}-?[0-9]{8}|[0-9]{2}-?[0-9]{7})$/.test(testEin);
            const isValidSsn = isValidSocialSecurity(ein, isRequired);
            return isEinValid || isValidSsn;
        }

        function isValidBillingStatementDescriptor(descriptor, isRequired) {
            if (!descriptor && !isRequired) return true;
            const testDescriptor = descriptor || '';

            return /^[\x20-\x7E]+$/.test(testDescriptor) && // all printable ascii characters
                testDescriptor.length <= this.DESCRIPTOR_MAX_LEN;
        }

        function isValidUrl(url, isRequired) {
            if (!url && !isRequired) return true;
            return isNonEmptyString(url) && startsWith(url, 'http');
        }

        function isValidNumber(number, isRequired) {
            if (!number && !isRequired) return true;
            const numberRegex = /^[0-9]*$/;
            return numberRegex.test(number);
        }

        function bankRoutingRegex() {
            return /^[0-9]{9}$/;
        }

        function bankAccountRegex() {
            return /^[0-9a-zA-Z]{3,17}$/;
        }

        function isValidBankAccount(accountNumber) {
            if (!accountNumber) return false;
            return bankAccountRegex().test(accountNumber);
        }

        function isValidRoutingNumber(routingNumber) {
            return bankRoutingRegex().test(routingNumber);
        }

        function isStepValid(step, model) {
            switch (step) {
                case this.steps.BUSINESS_IDENTITY:
                    return validateBusinessIdentity(model.business_identity).isValid;
                case this.steps.ABOUT_BUSINESS:
                    return validateAboutBusiness(model.about_business).isValid;
                case this.steps.BUSINESS_OWNER:
                    return validateOwner(model.owner).isValid;
                case this.steps.CARD_STATEMENT:
                    return this.validateCardStatement(model.card_statement).isValid;
                case this.steps.BANKING:
                    return validateBanking(model.banking).isValid;
                default:
                    return false;
            }
        }

        function makeKYCModel(data) { // data is optional
            return modelService.makeModel(data, 'volusionPaymentsKYCModel');
        }

        function prePopulateKYCModel(storeInfo, accountInfo, addresses, subscription) {
            const business = this.makeKYCModel();
            business.subscriptionId = subscription.id;
            const address = getPrimaryAddress(addresses, storeInfo);

            // business identity
            const identity = business.business_identity;
            identity.business_name = storeInfo.name;
            identity.business_website = storeInfo.customDomain ? `https://${storeInfo.customDomain}` : null;
            identity.business_phone.phone_number = storeInfo.phoneNumber || accountInfo.phoneNumber;
            identity.address = prePopulateAddress(identity.address, address);

            // owner
            const owner = business.owner;
            if (accountInfo.firstName) owner.owner_name.first = accountInfo.firstName;
            if (accountInfo.lastName) owner.owner_name.last = accountInfo.lastName;
            if (accountInfo.email) owner.email = accountInfo.email;
            owner.owner_phone.phone_number = accountInfo.phoneNumber || storeInfo.phoneNumber;
            owner.owner_address = prePopulateAddress(owner.owner_address, address);

            // card statement
            const descriptor = storeInfo.name.length > this.DESCRIPTOR_MAX_LEN ?
                storeInfo.name.substring(0, this.DESCRIPTOR_MAX_LEN) : storeInfo.name;

            business.card_statement.billing_statement_descriptor = descriptor;
            business.card_statement.customer_service_phone.phone_number = storeInfo.phoneNumber;

            return business;
        }

        function getPrimaryAddress(addresses, storeInfo) {
            if (addresses && addresses.length > 0) {
                return addresses.filter((address) => {
                    return address.isPrimary;
                })[0].mailingAddress;
            }
            return storeInfo.mailingAddress;
        }

        function prePopulateAddress(target, source) {
            const sourceFields = {
                address1: source.address1,
                address2: source.address2,
                city: source.city,
                region: source.state,
                postal_code: source.postalCode
            };
            return assign(target, sourceFields);
        }

        function getPrePopulationData(model) {
            const identity = model.business_identity || {};
            const identity_address = identity.address || {}; // eslint-disable-line camelcase
            const identity_phone = identity.business_phone || {}; // eslint-disable-line camelcase

            const owner = model.owner || {};
            const owner_name = owner.owner_name || {}; // eslint-disable-line camelcase
            const owner_phone = owner.owner_phone || {}; // eslint-disable-line camelcase

            return {
                address1: !!identity_address.address1,
                address2: !!identity_address.address2,
                city: !!identity_address.city,
                state: !!identity_address.region,
                zip: !!identity_address.postal_code,

                identityName: !!identity.business_name,
                identityPhone: !!identity_phone.phone_number,
                identityWebsite: !!identity.business_website,

                email: !!owner.email,
                ownerFirstName: !!owner_name.first,
                ownerLastName: !!owner_name.last,
                ownerPhone: !!owner_phone.phone_number,

                billingStatementDescriptor: !!model.card_statement.billing_statement_descriptor,
                customerServicePhone: !!model.card_statement.customer_service_phone.phone_number
            };
        }

        function badValueCheck({ form, field, validate, badValues }) {
            let isValid = true;
            if (badValues.length > 0) {
                isValid = validate();
                $timeout(() => { // wait for content to render
                    this.molestField(form, field);
                });
            }
            return isValid;
        }

        function showFieldErrors(form, step) {
            if (step === this.steps.BUSINESS_IDENTITY) {
                molestFields(form, this.formFields.BUSINESS_IDENTITY);
            }
            if (step === this.steps.ABOUT_BUSINESS) {
                molestFields(form, this.formFields.ABOUT_BUSINESS);
            }
            if (step === this.steps.BUSINESS_OWNER) {
                molestFields(form, this.formFields.BUSINESS_OWNER);
            }
            if (step === this.steps.CARD_STATEMENT) {
                molestFields(form, this.formFields.CARD_STATEMENT);
            }
            if (step === this.steps.BANKING) {
                molestFields(form, this.formFields.BANKING);
            }
        }

        function molestFields(form, fields) {
            return fields.map((field) => {
                return molestField(form, field);
            });
        }

        function molestField(form, field) {
            if (!form || !form[field]) return;
            form[field].$setTouched();
            form[field].$setDirty();
        }

        function basicDialogController(scopeVars) {
            return function DialogController($scope, $mdDialog) { // eslint-disable-line no-shadow
                'ngInject'; // eslint-disable-line lines-around-directive

                assign($scope, scopeVars);

                $scope.closeDialog = function() {
                    $mdDialog.hide();
                };
            };
        }

        function updateSignupSubmitted() {
            const props = {
                isPaymentsSignupSubmitted: true
            };
            return assignToStoreInfoAuthorizeNet(props);
        }

        function updateAuthorizeNetEnabled(isEnabled) {
            const props = {
                isEnabled,
                isStripePreferred: !isEnabled
            };
            return assignToStoreInfoAuthorizeNet(props).then(expireCache);
        }

        function updateStripePreferred(isPreferred) {
            const props = {
                isStripePreferred: isPreferred,
                isEnabled: !isPreferred
            };
            return assignToStoreInfoAuthorizeNet(props).then(expireCache);
        }

        function refreshStoreInfo() {
            return getFreshStoreInfoModel();
        }

        function getFreshStoreInfoModel() {
            const force = true;
            return storeInformationService.getStore(force)
                .then(response => modelService.makeModel(response, 'storeInformationModel'));
        }

        function isWhitelisted(email) {
            return /^spinaltaptest[0-9]+@gmail.com$/.test(email) || /@volusion.com$/.test(email) ||
                /@lolay.com$/.test(email);
        }

        function hasAccount(storeInfo) {
            return !!get(storeInfo, 'authorizeNet.isAccountCreated');
        }

        function hasOauthConnection({ storeInfo, authorizeNet }) {
            const authNet = authorizeNet || get(storeInfo, 'authorizeNet');
            return !!get(authNet, 'accessToken') && !!get(authNet, 'accessTokenExpiration');
        }

        function getUserState(storeInfo) {
            let userState;
            if (this.hasOauthConnection({ storeInfo })) {
                userState = this.userStates.COMPLETE;
            } else if (this.hasAccount(storeInfo)) {
                userState = this.userStates.READY_FOR_OAUTH;
            } else {
                userState = this.userStates.SIGN_UP;
            }
            return userState;
        }

        function getOauthUrl(stateParam) {
            const base = `${process.env.AUTHNET_OAUTH_DOMAIN}/oauth/authorize?sub=oauth`;
            const client = `&client_id=${process.env.AUTHNET_CLIENT_ID}&state=${stateParam}&scope=read,write`;
            const redirect = `&redirect_uri=${process.env.BASE_ADMIN_URL}/settings/volusion-payments`;
            return `${base}${client}${redirect}`;
        }

        function getLoginUrl() {
            return process.env.AUTHNET_LOGIN;
        }

        function getTestModeUrl() {
            return `${process.env.AUTHNET_OAUTH_DOMAIN}${process.env.AUTHNET_TEST_MODE}`;
        }

        function saveAccountCreated() {
            return saveAccountSignupStatus(true);
        }

        function saveAccountDenied() {
            return saveAccountSignupStatus(false);
        }

        function saveAccountSignupStatus(isCreated) {
            const props = {
                isAccountCreated: !!isCreated,
                isAccountDenied: !isCreated
            };
            return assignToStoreInfoAuthorizeNet(props);
        }

        function savePaymentsSignupStarted() {
            const props = {
                isPaymentsSignupStarted: true
            };
            return assignToStoreInfoAuthorizeNet(props);
        }

        function isPaymentSignupReminderNeeded(storeInformation) {
            const started = get(storeInformation, 'authorizeNet.isPaymentsSignupStarted');
            const finished = get(storeInformation, 'authorizeNet.isPaymentsSignupSubmitted');
            return !!(started && !finished);
        }

        function shouldShowAuthorizeNet(storeInformation) {
            return get(storeInformation, 'authorizeNet.isPaymentsSignupStarted', false) ||
                !get(storeInformation, 'authorizeNet.isStripePreferred');
        }

        function isInTestMode() {
            return dataService.get(settings.api.volusionPaymentsTestMode)
                .then((response) => {
                    return response.isTestMode;
                })
                .catch((err) => {
                    if (err.status === 409) {
                        return isInTestMode();
                    }
                });
        }

        function goToAuthorizeNetOauth({
            state = uuid(),
            isExisting = false
        } = {}) {
            const oauthState = isExisting ? `existing-${state}` : state;
            const url = getOauthUrl(oauthState);
            const props = {
                oauthState
            };
            return assignToStoreInfoAuthorizeNet(props)
                .then(() => {
                    return $window.open(url, '_self');
                });
        }

        function completeOauth(code, state) {
            return getFreshStoreInfoModel()
                .then((storeInformation) => {
                    const isExistingOauthState = startsWith(state, 'existing');
                    if (isExistingOauthState !== storeInformation.authorizeNet.isExistingAccount) {
                        const err = { data: { items: [{ message: 'Unclear if Volusion Payments or Authorize.net' }] } };
                        return $q.reject(err);
                    }
                    if (state !== storeInformation.authorizeNet.oauthState) {
                        const err = { data: { items: [{ message: 'Invalid OAuth state param.' }] } };
                        return $q.reject(err);
                    }
                    return dataService.get(settings.api.volusionPaymentsComplete, code);
                });
        }

        function getOauthError(err) {
            const defaultError = 'Unknown error.';
            if (err && err.data && err.data.items && err.data.items.length > 0) {
                return err.data.items[0].message || defaultError;
            }
            return defaultError;
        }

        function setShouldShowConnectedMessage(shouldShow) {
            const props = {
                shouldShowConnectedMessage: shouldShow
            };
            return assignToStoreInfoAuthorizeNet(props);
        }

        function setIsEnabled(isEnabled) {
            const props = {
                isEnabled
            };
            return assignToStoreInfoAuthorizeNet(props).then(expireCache);
        }

        function disconnectAuthorizeNet() {
            const props = {
                accessToken: '',
                accessTokenExpiration: '',
                isAccountCreated: true,
                isAccountDenied: false,
                isEnabled: false,
                isPaymentsSignupStarted: true,
                isPaymentsSignupSubmitted: true,
                isStripePreferred: false,
                refreshToken: '',
                refreshTokenExpiration: '',
                shouldShowConnectedMessage: false,
                publicClientKey: '',
                apiLoginId: '',
                wasDisconnected: true
            };
            return assignToStoreInfoAuthorizeNet(props).then(expireCache);
        }

        function assignToStoreInfoAuthorizeNet(obj) {
            return getFreshStoreInfoModel()
                .then((storeInfo) => {
                    assign(storeInfo.authorizeNet, obj);
                    return storeInformationService.save(storeInfo);
                });
        }

        function getActiveProvider(authorizeNetData = {}) {
            if (authorizeNetData.isStripePreferred) return this.paymentProviders.STRIPE;
            if (authorizeNetData.isExistingAccount) return this.paymentProviders.AUTHORIZE_NET;
            return this.paymentProviders.VOLUSION_PAYMENTS;
        }

        function setActiveProvider(provider, oldAuthorizeNet) {
            let props = {
                shouldShowConnectedMessage: false
            };
            const isAuthorizeNetConnected = this.hasOauthConnection({ authorizeNet: oldAuthorizeNet });
            if (provider === this.paymentProviders.STRIPE) {
                props.isStripePreferred = true;
                props.isEnabled = false;
            } else {
                props.isExistingAccount = provider === this.paymentProviders.AUTHORIZE_NET;
                if (isAuthorizeNetConnected) {
                    props.isStripePreferred = false;
                    props.isEnabled = true;
                }
            }

            const switchFromAuthnetToVolusionPayments =
                provider === this.paymentProviders.VOLUSION_PAYMENTS && oldAuthorizeNet.isExistingAccount;
            const switchFromVolusionPaymentsToAuthnet =
                provider === this.paymentProviders.AUTHORIZE_NET && !oldAuthorizeNet.isExistingAccount;

            if (switchFromAuthnetToVolusionPayments || switchFromVolusionPaymentsToAuthnet) {
                props.accessToken = '';
                props.accessTokenExpiration = '';
                props.isAccountCreated = false;
                props.isAccountDenied = false;
                props.isPaymentsSignupStarted = true;
                props.isPaymentsSignupSubmitted = false;
                props.isTestMode = false;
                props.refreshToken = '';
                props.refreshTokenExpiration = '';
                props.publicClientKey = '';
                props.apiLoginId = '';
                props.wasDisconnected = false;
            }
            return assignToStoreInfoAuthorizeNet(props).then(expireCache);
        }

        function getBadgeText(authorizeNet, currentStep) {
            const isConnected = this.hasOauthConnection({ authorizeNet });
            if (!authorizeNet) return 'Inactive';
            if (authorizeNet.wasDisconnected && !authorizeNet.isEnabled && !isConnected) {
                return 'Inactive';
            }
            const isComplete = currentStep >= this.userStates.COMPLETE;
            if (!isComplete && authorizeNet.isPaymentsSignupSubmitted) return 'Pending';
            if (isComplete &&
                !authorizeNet.isEnabled &&
                authorizeNet.isStripePreferred
            ) return 'Disabled';
            if (isComplete && !authorizeNet.isEnabled) return 'Checkout Disabled';
            if (isComplete && authorizeNet.isTestMode) return 'Test Mode';
            if (isComplete) return 'Active';
            return 'Inactive';
        }
    }).name;



// WEBPACK FOOTER //
// ./src/app/services/data/volusionPayments.service.js